import React, { useState, useLayoutEffect, useMemo, useRef, UIEvent, useCallback } from 'react'
import noop from 'lodash/noop'

import { getResponsiveImgProps } from 'src/comps/Gallery/utils'
import useGtmEvent from 'src/hooks/useGtmEvent'
import { ImageApiObject } from 'src/types/image'

import NavigationButton from 'src/comps/Atom/NavigationButton'

import { PhotosContainer, NavigationContainer, PhotosZoomBase, Slider, SliderItem } from './styles'
import { IMAGE_LOADING, IMAGE_LOADING_EAGER, IMAGE_LOADING_LAZY } from 'src/comps/Image/constants'
import { rtlLeftNormalizer } from '@s4l/utils/library/rtl'

const sizes = {
  desktop: {
    size: '1200px',
    srcWidth: 1200,
  },
  tablet: {
    size: '847px',
    srcWidth: 847,
  },
  mobile: {
    size: '100vw',
    srcWidth: 727,
  },
}

interface Props {
  focusOnImageIndex?: number
  photos: ImageApiObject[]
  onChange?: (index: number) => void
  className?: string
}

const PhotosCarousel = ({ focusOnImageIndex = 0, onChange = noop, photos, className }: Props) => {
  const sliderRef = useRef<HTMLDivElement>(null)
  const [currentSlide, setCurrentSlide] = useState(focusOnImageIndex)
  const { notify } = useGtmEvent({ eventName: 'click_to_slide' })

  const firstSlide = currentSlide === 0
  const lastSlide = currentSlide === photos.length - 1

  const photosWithLoadingType = useMemo(
    () =>
      photos.map((photo, index) => {
        const loading: (typeof IMAGE_LOADING)[number] =
          index === currentSlide || index - 1 === currentSlide || index + 1 === currentSlide
            ? IMAGE_LOADING_EAGER
            : IMAGE_LOADING_LAZY
        return {
          ...getResponsiveImgProps(photo, sizes),
          loading,
        }
      }),
    [photos, currentSlide]
  )

  const handleNavigation = (index: number) => {
    onChange(index)
    setCurrentSlide(index)
  }

  const handleNotify = useCallback(
    (index: number) => {
      if (index !== focusOnImageIndex) {
        notify()
      }
    },
    [focusOnImageIndex, notify]
  )

  const scrollHandler = (event: UIEvent<HTMLDivElement>) => {
    const { offsetWidth, scrollLeft } = event.currentTarget

    if (scrollLeft % offsetWidth === 0) {
      const index = rtlLeftNormalizer(scrollLeft / offsetWidth)
      handleNavigation(index)
    }
  }

  useLayoutEffect(() => {
    if (!sliderRef.current) return

    const { width } = sliderRef.current.getBoundingClientRect()
    if (sliderRef.current.scrollTo) {
      sliderRef.current.scrollTo({
        left: rtlLeftNormalizer(width * currentSlide),
      })
    }
    handleNotify(currentSlide)
  }, [currentSlide, handleNotify])

  return (
    <PhotosZoomBase className={className} data-testid="photos-zoom">
      <NavigationContainer>
        {!firstSlide && (
          <NavigationButton
            direction="left"
            data-testid="prevButton"
            onClick={() => handleNavigation(currentSlide - 1)}
          />
        )}
      </NavigationContainer>
      <PhotosContainer>
        <Slider ref={sliderRef} onScroll={scrollHandler}>
          {photosWithLoadingType.map((photo, index) => (
            <SliderItem
              alt={`photo-${index + 1}`}
              key={`photo-${index + 1}`}
              id={`photo-${index + 1}`}
              photo={photo}
            />
          ))}
        </Slider>
      </PhotosContainer>
      <NavigationContainer>
        {!lastSlide && (
          <NavigationButton
            direction="right"
            data-testid="nextButton"
            onClick={() => handleNavigation(currentSlide + 1)}
          />
        )}
      </NavigationContainer>
    </PhotosZoomBase>
  )
}

export default PhotosCarousel
